import { apiBaseURL } from "./config"
// import * as sentry from "./sentry"
import * as SSR from "../../api/hooks/lib/ssr.js"
import { navstore, pfadstore, sprache } from "./store"

// [MIT License](LICENSE.md) © [Jason Miller](https://jasonformat.com/)
const _f = function (url, options): Promise<Response> {
    if (typeof XMLHttpRequest === "undefined") {
        return Promise.resolve(null)
    }

    options = options || {}
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        const keys = []
        const all = []
        const headers = {}

        const response = (): Response => ({
            ok: ((request.status / 100) | 0) == 2, // 200-299
            statusText: request.statusText,
            status: request.status,
            url: request.responseURL,
            text: () => Promise.resolve(request.responseText),
            json: () => Promise.resolve(request.responseText).then(JSON.parse),
            blob: () => Promise.resolve(new Blob([request.response])),
            clone: response,
            headers: {
                // @ts-ignore
                keys: () => keys,
                // @ts-ignore
                entries: () => all,
                get: (n) => headers[n.toLowerCase()],
                has: (n) => n.toLowerCase() in headers,
            },
        })

        request.open(options.method || "get", url, true)

        request.onload = () => {
            request
                .getAllResponseHeaders()
                // @ts-ignore
                .replace(/^(.*?):[^\S\n]*([\s\S]*?)$/gm, (m, key, value) => {
                    keys.push((key = key.toLowerCase()))
                    all.push([key, value])
                    headers[key] = headers[key]
                        ? `${headers[key]},${value}`
                        : value
                })
            resolve(response())
        }

        request.onerror = reject

        request.withCredentials = options.credentials == "include"

        for (const i in options.headers) {
            request.setRequestHeader(i, options.headers[i])
        }

        request.send(options.body || null)
    })
}

const _fetch =
    typeof fetch === "undefined"
        ? typeof window === "undefined"
            ? _f
            : window.fetch || _f
        : fetch

export const api = async <T>(
    endpoint: string,
    options?: {
        method?: string
        filter?: any
        sort?: string
        limit?: number
        offset?: number
        projection?: string
        headers?: {
            [key: string]: string
        }
        params?: {
            [key: string]: string
        }
    }
): Promise<{ data: T; count: number }> => {
    if (typeof window === "undefined") {
        // ssr
        // @ts-ignore
        return context.ssrFetch(endpoint, options)
    }

    // try ssr cache
    const key = SSR.obj2str({ endpoint, options })
    // @ts-ignore
    if (window.__SSR_CACHE__) {
        // @ts-ignore
        const ssrVal = window.__SSR_CACHE__[key]
        console.log("SSR:", key, ssrVal)
        if (ssrVal) {
            return ssrVal
        }
    }

    let method = "GET"

    let query = "&count=1"
    if (options?.filter)
        query += "&filter=" + encodeURIComponent(JSON.stringify(options.filter))
    if (options?.sort) query += "&sort=" + options.sort + "&sort=_id"
    if (options?.limit) query += "&limit=" + options.limit
    if (options?.offset) query += "&offset=" + options.offset
    if (options?.projection) query += "&projection=" + options.projection

    if (options?.params) {
        Object.keys(options.params).forEach((p) => {
            query += "&" + p + "=" + encodeURIComponent(options.params[p])
        })
    }

    let headers: any = {
        "Content-Type": "application/json",
    }

    if (options?.headers) headers = { ...headers, ...options.headers }

    let url = apiBaseURL + endpoint + (query ? "?" + query : "")

    // const span = sentry.currentTransaction()?.startChild({
    //     op: "fetch",
    //     description: method + " " + url,
    //     data: Object.assign({}, options, { url }),
    // })
    // const trace_id = span?.toTraceparent()
    // if (trace_id) {
    //     headers["sentry-trace"] = trace_id
    // }

    const response = await _fetch(url, {
        method,
        mode: "cors",
        headers,
    })

    // span?.finish()

    // @ts-ignore
    let data = (await response?.json()) || null

    if (response?.status < 200 || response?.status >= 400)
        throw { response, data }

    // @ts-ignore
    return { data, count: response?.headers?.get("x-results-count") || 0 }
}

export const getSeite = async (pfad: string): Promise<Seite> => {
    const r = await api<Seite[]>("seiten", {
        filter: {
            $or: [{ pfad }, { "alternativePfade.pfad": pfad }],
        },
    })
    if (r.data.length) return r.data[0]
    return null
}

export const getSeiten = async (
    sprache: string,
    suche: string = null,
    typ: string = null,
    limit: number = null,
    projection: string = "list",
    additionalFilter: any = null,
    sort: string = "-sortierung"
) => {
    let filter: any = {
        sprachen: sprache,
        aktiv: true,
        archiv: { $ne: true },
    }
    if (typ) {
        filter.typ = typ
    }
    if (suche) {
        filter["$text"] = { $search: suche }
        filter["aktiv"] = true
        filter["meta.nichtSuchbar"] = { $ne: true }
    }
    if (additionalFilter) {
        filter = Object.assign({}, filter, additionalFilter)
    }
    const r = await api<Seite[]>("seiten", {
        filter,
        limit,
        projection,
        sort: suche ? null : sort,
    })
    return r?.data
}

export const getPersonen = async (
    team?: boolean,
    kuerzel?: string[]
): Promise<Person[]> => {
    let filter: any
    filter = { $and: [] }
    if (team || kuerzel) {
        if (team) {
            filter["$and"].push({ team: true })
        }
        if (kuerzel) {
            filter["$and"].push({ kuerzel: { $in: kuerzel } })
        } else {
            // nur aktive im listing
            filter["$and"].push({
                $or: [{ aktiv: { $exists: false } }, { aktiv: true }],
            })
        }
    }

    return (
        await api<Person[]>("personen", {
            sort: "sortierung",
            filter,
        })
    ).data
}

async function ladeNavigation(lang: string) {
    const s = await api<Seite[]>("seiten", {
        filter: {
            sprachen: lang,
        },
        projection: "selectbox",
    })

    const sMap: { [id: string]: string } = {}
    s.data?.forEach((s) => {
        sMap[s.id] = s.pfad
    })

    const r = await api<Navigation[]>("navigation", {
        filter: { sprache: lang },
    })
    const nav: { [key: number]: NavElement[] } = {}
    const pfade: { [pfad: string]: NavElement } = {}

    function link2Elemente(elemente: NavElement[], parent: NavElement = null) {
        elemente?.forEach((e) => {
            e.link = sMap[e.seite]
            e.parent = parent
            pfade[e.link] = e
            link2Elemente(e.elemente, e)
        })
    }

    r.data?.forEach((n) => {
        link2Elemente(n.elemente)
        nav[n.baum] = n.elemente
    })
    navstore.set(nav)
    pfadstore.set(pfade)
}

// let oldSprache = ""
sprache.subscribe((s) => {
    // if (s && s != oldSprache) {
    //     oldSprache = s
    //     loadNavi(s)
    // }
    ladeNavigation(s)
})
